import { DEFAULT_KIT_ID } from "./kits";
import type { OnboardingFormValues } from "../hooks/useFormData";
import type {
  SetupStyling,
  SetupOperation,
  SetupComponents,
  SetupVoice,
  ApplyToFramesResponse,
} from "@/types/onboarding";

// Persists in-progress onboarding state to localStorage so a reload, a new tab,
// or a recoverable failure resumes where the user left off — without re-running
// the ~90s AI generation or losing local edits. Scoped per siteId so concurrent
// onboardings in different tabs never collide.

export const ONBOARDING_DRAFT_VERSION = 1;
export const ONBOARDING_DRAFT_KEY_PREFIX = "qo_onboarding_draft_v1:";
export const ONBOARDING_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Steps whose data we persist. `intro` is saved server-side synchronously
// before navigation, so it needs no draft.
export type OnboardingDraftStep =
  | "styling"
  | "operation"
  | "components"
  | "voice";

export interface OnboardingDraft {
  version: number;
  savedAt: number;
  // When `generated` was last written by a re-map. savedAt moves on every
  // draft write (edits, per-step clears), so it can't date the generation.
  generatedAt?: number;
  domain?: string;
  // Raw AI-generated step payloads (server shape). Used to re-seed the cache.
  generated?: {
    styling?: SetupStyling;
    operation?: SetupOperation;
    // No AI generation writes this yet — typed so OnboardingDraftStep indexing
    // stays sound and a future components generation slots in.
    components?: SetupComponents;
    voice?: SetupVoice;
  };
  // In-progress, user-edited values (form shape, post getInitialValues transform).
  edits?: Partial<Record<OnboardingDraftStep, OnboardingFormValues>>;
  // Field ids still borrowing an inheritFrom sibling's value as of the last
  // `edits` write (see BrandKitField.inheritFrom / useFormData's
  // stillInheritedFieldIds) — only the styling step has inheritFrom fields.
  // SaveProgressButton reads this back so flushing a draft (no live form, no
  // live ref) still writes them "" instead of the resolved value in `edits`.
  stillInherited?: Partial<Record<OnboardingDraftStep, string[]>>;
  // Apply scope chosen on the "Select tiles" step. Read by the complete step to
  // restrict applyToFrames to the selected frames; reload-safe so a refresh
  // between the scope and complete steps keeps the selection. `all` applies to
  // every frame (frameIds omitted from the request).
  applyScope?: {
    mode: "all" | "selected";
    frameIds?: string[]; // present iff mode === "selected"
    // `force_copy`: regenerate tile copy even when the brand voice is
    // unchanged since the last apply (the backend otherwise skips copy
    // site-wide, so newly added tiles get none).
    forceCopy?: boolean;
    // `force_subject_line_preview_text`: overwrite existing subject lines and
    // preview text with generated copy (independent of forceCopy).
    forceSubjectLinePreviewText?: boolean;
    savedAt: number;
  };
  // Outcome of the last applyToFrames run, persisted so the results step survives
  // a reload / new tab without re-running the expensive apply.
  // Carries the run's copy flags so a retry after a reload re-sends them (the
  // applyScope that held them is cleared once the apply succeeds).
  applyResult?: {
    status: "success" | "error";
    response?: ApplyToFramesResponse; // present on success / partial failure
    error?: string; // present on error
    savedAt: number;
  } & ApplyCopyFlags;
}

// The opt-in copy flags of an apply run (`force_copy`,
// `force_subject_line_preview_text`).
export type ApplyCopyFlags = {
  forceCopy?: boolean;
  forceSubjectLinePreviewText?: boolean;
};

// Only the flags that are on, so unset flags never land in a payload or draft.
export function pickCopyFlags(source?: ApplyCopyFlags): ApplyCopyFlags {
  return {
    ...(source?.forceCopy && { forceCopy: true }),
    ...(source?.forceSubjectLinePreviewText && {
      forceSubjectLinePreviewText: true,
    }),
  };
}

function storageAvailable(): boolean {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch {
    return false;
  }
}

export function draftKey(siteId: string, kit: string = DEFAULT_KIT_ID): string {
  // Default kit keeps the pre-kit key so existing drafts survive the upgrade;
  // named kits get their own bucket so drafts never bleed across kits.
  const scope = kit === DEFAULT_KIT_ID ? siteId : `${siteId}:${kit}`;
  return `${ONBOARDING_DRAFT_KEY_PREFIX}${scope}`;
}

export function readDraft(
  siteId: string,
  kit?: string,
): OnboardingDraft | null {
  if (!storageAvailable() || !siteId) return null;
  try {
    const raw = window.localStorage.getItem(draftKey(siteId, kit));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as OnboardingDraft;

    // Version bust + staleness cap. Either invalidates the draft.
    const isStale =
      typeof parsed.savedAt !== "number" ||
      Date.now() - parsed.savedAt > ONBOARDING_DRAFT_MAX_AGE_MS;
    if (parsed.version !== ONBOARDING_DRAFT_VERSION || isStale) {
      clearDraft(siteId, kit);
      return null;
    }

    return parsed;
  } catch {
    // Corrupt JSON — drop it so the flow starts clean.
    clearDraft(siteId, kit);
    return null;
  }
}

export function writeDraft(
  siteId: string,
  draft: OnboardingDraft,
  kit?: string,
): void {
  if (!storageAvailable() || !siteId) return;
  try {
    window.localStorage.setItem(draftKey(siteId, kit), JSON.stringify(draft));
  } catch {
    // Quota / private mode — degrade silently to in-memory behavior.
  }
}

export function clearDraft(siteId: string, kit?: string): void {
  if (!storageAvailable() || !siteId) return;
  try {
    window.localStorage.removeItem(draftKey(siteId, kit));
  } catch {
    // ignore
  }
}

// Removes every onboarding draft (all sites, all key versions) — used to start
// a fresh session clean on login. Matches on the un-versioned base prefix so
// legacy / future versioned keys are swept too.
export function clearAllOnboardingDrafts(): void {
  if (!storageAvailable()) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith("qo_onboarding_draft")) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

function baseDraft(siteId: string, kit?: string): OnboardingDraft {
  return (
    readDraft(siteId, kit) ?? {
      version: ONBOARDING_DRAFT_VERSION,
      savedAt: Date.now(),
    }
  );
}

export function patchGenerated(
  siteId: string,
  generated: OnboardingDraft["generated"],
  domain?: string,
  kit?: string,
): void {
  if (!siteId) return;

  const existing = readDraft(siteId, kit);
  // If generation re-ran for a different brand domain, discard the previous
  // brand's draft entirely so its stale generated data and in-progress edits
  // can't bleed into this run.
  const draft =
    existing &&
    domain !== undefined &&
    existing.domain !== undefined &&
    existing.domain !== domain
      ? null
      : existing;

  // Spread the surviving draft first so unrelated fields (applyScope,
  // applyResult) aren't silently dropped by a regeneration pass.
  const now = Date.now();
  writeDraft(
    siteId,
    {
      ...draft,
      version: ONBOARDING_DRAFT_VERSION,
      savedAt: now,
      generatedAt: now,
      domain: domain ?? draft?.domain,
      generated: { ...draft?.generated, ...generated },
      edits: draft?.edits,
    },
    kit,
  );
}

export function patchEdits(
  siteId: string,
  step: OnboardingDraftStep,
  values: OnboardingFormValues,
  kit?: string,
  // Appended after `kit` (rather than reordering) so existing positional
  // calls keep working. Omit to leave any previously-saved stillInherited
  // entry for this step untouched; pass [] to explicitly clear it.
  stillInheritedIds?: readonly string[],
): void {
  if (!siteId) return;
  const draft = baseDraft(siteId, kit);
  writeDraft(
    siteId,
    {
      ...draft,
      version: ONBOARDING_DRAFT_VERSION,
      savedAt: Date.now(),
      edits: { ...draft.edits, [step]: values },
      stillInherited:
        stillInheritedIds === undefined
          ? draft.stillInherited
          : { ...draft.stillInherited, [step]: [...stillInheritedIds] },
    },
    kit,
  );
}

// Persists the outcome of an applyToFrames run so the results step can read it
// after a reload / new tab without re-running the apply.
export function patchApplyResult(
  siteId: string,
  applyResult: OnboardingDraft["applyResult"],
  kit?: string,
): void {
  if (!siteId) return;
  const draft = baseDraft(siteId, kit);
  writeDraft(
    siteId,
    {
      ...draft,
      version: ONBOARDING_DRAFT_VERSION,
      savedAt: Date.now(),
      applyResult,
    },
    kit,
  );
}

// Persists the apply scope chosen on the "Select tiles" step so the complete
// step (and a mid-apply reload) applies to the same selection.
export function patchApplyScope(
  siteId: string,
  applyScope: OnboardingDraft["applyScope"],
  kit?: string,
): void {
  if (!siteId) return;
  const draft = baseDraft(siteId, kit);
  writeDraft(
    siteId,
    {
      ...draft,
      version: ONBOARDING_DRAFT_VERSION,
      savedAt: Date.now(),
      applyScope,
    },
    kit,
  );
}

// Drops the apply scope — called once an apply succeeds so a later direct visit
// to the complete step falls back to applying all frames.
export function clearApplyScope(siteId: string, kit?: string): void {
  if (!siteId) return;
  const draft = readDraft(siteId, kit);
  if (!draft?.applyScope) return;

  const rest = { ...draft };
  delete rest.applyScope;
  writeDraft(
    siteId,
    {
      ...rest,
      savedAt: Date.now(),
    },
    kit,
  );
}

// Drops both the generated payload and edits for a single step — used once the
// step is saved to the server, so the server becomes the source of truth.
export function clearStep(
  siteId: string,
  step: OnboardingDraftStep,
  kit?: string,
): void {
  if (!siteId) return;
  const draft = readDraft(siteId, kit);
  if (!draft) return;

  const generated = { ...draft.generated };
  delete generated[step];
  const edits = { ...draft.edits };
  delete edits[step];
  const stillInherited = { ...draft.stillInherited };
  delete stillInherited[step];

  writeDraft(
    siteId,
    {
      ...draft,
      savedAt: Date.now(),
      generated,
      edits,
      stillInherited,
    },
    kit,
  );
}

// Drops only the in-progress edits for a step, keeping the generated baseline —
// used by the per-step "Reset" button so a reset still survives a reload.
export function clearEdits(
  siteId: string,
  step: OnboardingDraftStep,
  kit?: string,
): void {
  if (!siteId) return;
  const draft = readDraft(siteId, kit);
  if (!draft?.edits?.[step]) return;

  const edits = { ...draft.edits };
  delete edits[step];
  const stillInherited = { ...draft.stillInherited };
  delete stillInherited[step];

  writeDraft(
    siteId,
    {
      ...draft,
      savedAt: Date.now(),
      edits,
      stillInherited,
    },
    kit,
  );
}
